import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type { PromptFragmentComposeStatus } from "@/api/types";

// ---- Per-org prompt fragments --------------------------------------

const promptFragmentsKey = (slug: string) =>
  ["orgs", slug, "prompt-fragments"] as const;

/** Where an org copy of a Repoworks style came from. */
export type PromptFragmentDerivedFrom = {
  slug: string;
  /** The platform version the copy is based on. */
  version: number;
  /** The running release's version; null when the style is gone. */
  current_version: number | null;
  status: "active" | "retired" | "missing";
  update_available: boolean;
};

export type PromptFragmentListItem = {
  id: string;
  kind: "style" | "base_kb" | "org_agents";
  /** Deprecated single target; read ``intents``. */
  intent: string | null;
  /** Target intents of a style; ``null`` = every session type. */
  intents: string[] | null;
  title: string;
  applies_by_default: boolean;
  is_active: boolean;
  version: number;
  updated_at: string | null;
  // When ``true`` the fragment body is supplied (and pinned) by an
  // RW_* environment variable on the host; the admin UI must lock its
  // mutating controls. ``env_var`` names the active variable so the UI
  // can tell the user where to change it. Mirrors the config-store
  // env-pin precedent (is_pinned / env_var).
  env_managed: boolean;
  env_var: string | null;
  /** Rendered size the agent receives (``### Style:`` header + body)
   *  for a style; the body length for the other kinds. */
  chars: number;
  derived_from: PromptFragmentDerivedFrom | null;
};

export type PromptFragmentView = PromptFragmentListItem & {
  body_md: string;
  updated_by: string | null;
  /** The platform body the copy is based on (three-way update base). */
  derived_base_body_md: string | null;
};

/** One Repoworks platform style from ``GET /prompt-library/platform``. */
export type PlatformStyle = {
  /** ``platform:<slug>``: the selection ref. */
  ref: string;
  slug: string;
  version: number;
  /** English title; the UI shows the localised name by ``slug``. */
  title: string;
  category: string;
  intents: string[];
  status: "active" | "retired";
  /** Rendered layer length (header + body). */
  chars: number;
  body_md: string;
};

export type PlatformCatalog = {
  catalog_sha: string;
  limits: { fragment_chars: number; total_style_chars: number };
  styles: PlatformStyle[];
};

export const platformCatalogKey = ["prompt-library", "platform"] as const;

/** The Repoworks style catalog of the running release. Cached for an
 *  hour; the prompt library refetches it when an org copy reports a
 *  newer version, and the update review before it builds a comparison. */
export function usePlatformCatalog(enabled = true) {
  return useQuery({
    queryKey: platformCatalogKey,
    queryFn: async () =>
      (await api.get<PlatformCatalog>("/prompt-library/platform")).data,
    enabled,
    staleTime: 60 * 60_000,
  });
}

/** The catalog, refetched on every mount: the Repoworks update review
 *  must never compare against a body cached before a deploy. Use
 *  ``isFetchedAfterMount`` before trusting ``data``. */
export function useFreshPlatformCatalog() {
  return useQuery({
    queryKey: platformCatalogKey,
    queryFn: async () =>
      (await api.get<PlatformCatalog>("/prompt-library/platform")).data,
    refetchOnMount: "always",
    staleTime: 60 * 60_000,
  });
}

export type DerivePromptFragmentPayload = {
  source: { platform_slug: string } | { fragment_id: string };
  title: string;
  intents?: string[] | null;
  applies_by_default?: boolean;
};

/** Take a newer Repoworks version into an org copy: with ``body_md``
 *  (the merged body from the update review) a revision is saved; without
 *  it the copy stays as it is. Either way its base moves to
 *  ``to_version``, which must be the running catalog's version (409
 *  ``platform_version_moved`` otherwise). */
export function useRebasePromptFragment(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      id: string;
      to_version: number;
      /** The copy's version the review was built on (409
       *  ``fragment_changed`` when it was saved since). */
      from_fragment_version: number;
      body_md?: string;
    }) => {
      const { id, ...body } = payload;
      return (
        await api.post<PromptFragmentView>(
          `/orgs/${slug}/prompt-fragments/${id}/rebase`,
          body,
        )
      ).data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: promptFragmentsKey(slug) }),
  });
}

/** Make an org copy of a Repoworks style, or duplicate an org fragment. */
export function useDerivePromptFragment(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: DerivePromptFragmentPayload) =>
      (
        await api.post<PromptFragmentView>(
          `/orgs/${slug}/prompt-fragments/derive`,
          payload,
        )
      ).data,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: promptFragmentsKey(slug) }),
  });
}

export type PromptFragmentRevisionItem = {
  version: number;
  title: string;
  created_at: string | null;
  created_by: string | null;
  created_by_name?: string | null;
};

export type PromptFragmentRevisionView = PromptFragmentRevisionItem & {
  body_md: string;
};

export function usePromptFragments(slug: string) {
  return useQuery({
    queryKey: promptFragmentsKey(slug),
    queryFn: async () =>
      (
        await api.get<PromptFragmentListItem[]>(
          `/orgs/${slug}/prompt-fragments`,
        )
      ).data,
    enabled: Boolean(slug),
    staleTime: 30_000,
  });
}

export type PromptFragmentEnvFeed = {
  slugs: string[];
  issues: { slug: string; source: string; reason: string }[];
};

/** What the env feed (RW_PROMPT_FRAGMENT__<slug>_B64 keys) looks like
 *  from inside the container — slugs in force plus keys that were
 *  skipped, so a typo in a dotenv shows up here and not only in logs. */
export function usePromptFragmentEnvFeed(slug: string) {
  return useQuery({
    queryKey: [...promptFragmentsKey(slug), "env-feed"] as const,
    queryFn: async () =>
      (
        await api.get<PromptFragmentEnvFeed>(
          `/orgs/${slug}/prompt-fragments/env-feed`,
        )
      ).data,
    enabled: Boolean(slug),
    staleTime: 60_000,
  });
}

export function usePromptFragment(slug: string, id: string | null) {
  return useQuery({
    queryKey: [...promptFragmentsKey(slug), id ?? ""] as const,
    queryFn: async () =>
      (
        await api.get<PromptFragmentView>(
          `/orgs/${slug}/prompt-fragments/${id}`,
        )
      ).data,
    enabled: Boolean(slug) && Boolean(id),
  });
}

/** One fragment, refetched on every mount whatever the cache holds: the
 *  Repoworks update review must compare against the copy as saved now,
 *  and the org pane has usually loaded it moments earlier, within the
 *  app's 30 s ``staleTime``. Use ``isFetchedAfterMount`` before trusting
 *  ``data``. */
export function useFreshPromptFragment(slug: string, id: string | null) {
  return useQuery({
    queryKey: [...promptFragmentsKey(slug), id ?? ""] as const,
    queryFn: async () =>
      (
        await api.get<PromptFragmentView>(
          `/orgs/${slug}/prompt-fragments/${id}`,
        )
      ).data,
    enabled: Boolean(slug) && Boolean(id),
    refetchOnMount: "always",
  });
}

export function useCreatePromptFragment(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      kind: "style" | "base_kb" | "org_agents";
      intent?: string | null;
      intents?: string[] | null;
      title: string;
      body_md: string;
      applies_by_default: boolean;
    }) =>
      (
        await api.post<PromptFragmentView>(
          `/orgs/${slug}/prompt-fragments`,
          payload,
        )
      ).data,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: promptFragmentsKey(slug) }),
  });
}

export function useUpdatePromptFragment(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      id: string;
      title?: string;
      body_md?: string;
      applies_by_default?: boolean;
      is_active?: boolean;
      /** Metadata: ``null`` = every session type; omit to leave alone. */
      intents?: string[] | null;
    }) => {
      const { id, ...body } = payload;
      return (
        await api.put<PromptFragmentView>(
          `/orgs/${slug}/prompt-fragments/${id}`,
          body,
        )
      ).data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: promptFragmentsKey(slug) }),
  });
}

export function useDeletePromptFragment(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/orgs/${slug}/prompt-fragments/${id}`);
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: promptFragmentsKey(slug) }),
  });
}

export function usePromptFragmentRevisions(slug: string, id: string | null) {
  return useQuery({
    queryKey: [...promptFragmentsKey(slug), id ?? "", "revisions"] as const,
    queryFn: async () =>
      (
        await api.get<PromptFragmentRevisionItem[]>(
          `/orgs/${slug}/prompt-fragments/${id}/revisions`,
        )
      ).data,
    enabled: Boolean(slug) && Boolean(id),
  });
}

export function usePromptFragmentRevision(
  slug: string,
  id: string | null,
  version: number | null,
) {
  return useQuery({
    queryKey: [
      ...promptFragmentsKey(slug),
      id ?? "",
      "revisions",
      version ?? -1,
    ] as const,
    queryFn: async () =>
      (
        await api.get<PromptFragmentRevisionView>(
          `/orgs/${slug}/prompt-fragments/${id}/revisions/${version}`,
        )
      ).data,
    enabled: Boolean(slug) && Boolean(id) && version != null,
  });
}

export function useRollbackPromptFragment(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { id: string; version: number }) =>
      (
        await api.post<PromptFragmentView>(
          `/orgs/${slug}/prompt-fragments/${payload.id}/rollback/${payload.version}`,
        )
      ).data,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: promptFragmentsKey(slug) }),
  });
}

// ---- Prompt-fragment compose (draft / refine with AI) --------------

/** One reference doc the composer can ground a run on — an org prompt
 *  fragment or a brand reference document. ``subtype`` is the "type" chip
 *  the picker renders (these references have no project field). */
export type ComposeReference = {
  kind: "fragment" | "brand_doc";
  id: string;
  title: string;
  subtype: string | null;
  updated_at: string | null;
  updated_by_name: string | null;
  /** Brand docs only: the brand profile the document belongs to. The
   *  API lists brand docs primary profile first, then by profile name.
   *  ``null`` on fragments (org-wide). */
  profile_id?: string | null;
  profile_name?: string | null;
};

/** The reference docs a compose run will ground on (the org's active
 *  fragments + its brand docs), surfaced so a manager can curate them.
 *  ``excludeFragmentId`` drops the fragment being refined so it isn't
 *  offered as its own reference. */
export function useComposeAvailableReferences(
  slug: string,
  excludeFragmentId?: string,
) {
  return useQuery({
    queryKey: [
      ...promptFragmentsKey(slug),
      "compose",
      "available-references",
      excludeFragmentId ?? "",
    ] as const,
    enabled: Boolean(slug),
    queryFn: async () =>
      (
        await api.get<ComposeReference[]>(
          `/orgs/${slug}/prompt-fragments/compose/available-references`,
          excludeFragmentId
            ? { params: { exclude_fragment_id: excludeFragmentId } }
            : undefined,
        )
      ).data,
    staleTime: 30_000,
  });
}

/** Kick off a compose run. ``draft`` creates a brand-new fragment from
 *  the instruction (with the chosen ``kind`` / ``intent``); ``refine``
 *  revises an existing ``fragment_id``. Returns the job id the caller
 *  polls with ``usePromptFragmentComposeJob``.
 *
 *  ``reference_fragment_ids`` / ``reference_brand_doc_ids`` carry the
 *  manager's curated reference selection: omit (undefined) to ground on
 *  ALL references (default), or pass explicit lists to ground only on
 *  those. */
export function useCreatePromptFragmentCompose(slug: string) {
  return useMutation({
    mutationFn: async (payload: {
      mode: "draft" | "refine";
      fragment_id?: string | null;
      kind?: "style" | "base_kb" | "org_agents" | null;
      intent?: string | null;
      instruction: string;
      /** Runtime the run uses; omitted → the server default (codex). */
      provider?: string;
      reference_fragment_ids?: string[];
      reference_brand_doc_ids?: string[];
    }) =>
      (
        await api.post<{ compose_id: string }>(
          `/orgs/${slug}/prompt-fragments/compose`,
          payload,
        )
      ).data,
  });
}

/** Poll an in-flight compose job; stops polling once it reaches a
 *  terminal status (ready / failed / applied / rejected). Mirrors the
 *  brand-compose polling predicate. */
export function usePromptFragmentComposeJob(slug: string, jobId: string | null) {
  return useQuery({
    queryKey: [
      ...promptFragmentsKey(slug),
      "compose",
      jobId ?? "",
    ] as const,
    enabled: Boolean(slug) && Boolean(jobId),
    queryFn: async () =>
      (
        await api.get<PromptFragmentComposeStatus>(
          `/orgs/${slug}/prompt-fragments/compose/${jobId}`,
        )
      ).data,
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === "queued" || s === "running" ? 2000 : false;
    },
    staleTime: 0,
  });
}

/** Accept a ready proposal — creates / revises the fragment and flips
 *  the job to ``applied``. Invalidates the fragment list so the new /
 *  bumped fragment shows up. When ``body_md`` is given it overrides the
 *  stored proposal (the merged body from the editable per-hunk diff);
 *  omit it to apply the proposal as-is. */
export function useApplyPromptFragmentCompose(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      jobId,
      body_md,
    }: {
      jobId: string;
      body_md?: string;
    }) =>
      (
        await api.post<PromptFragmentView>(
          `/orgs/${slug}/prompt-fragments/compose/${jobId}/apply`,
          { body_md },
        )
      ).data,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: promptFragmentsKey(slug) }),
  });
}

/** Discard a ready proposal — flips the job to ``rejected``. */
export function useRejectPromptFragmentCompose(slug: string) {
  return useMutation({
    mutationFn: async (jobId: string) => {
      await api.post(`/orgs/${slug}/prompt-fragments/compose/${jobId}/reject`);
    },
  });
}
